/**
 * Modal host for the Add/Edit/Copy Assignment component
 **/
import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {plural, t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, LabelXSmall} from "@khanacademy/wonder-blocks-typography";
import type {
    Assignment,
    NewAssignmentModalTitle,
    NewAssignmentSubmitParameters,
    UserHasChildAssignmentClassrooms,
} from "@khan/assignments-types";
import {
    getMaxDueDateWritingCoach,
    isAfterNow,
    isWritingCoachAssignment,
} from "@khan/assignments-utils";
import {getPass} from "@khan/data-cookies";
import {mediaQueries} from "@khan/shared-styles";

import ActivityCustomization from "./activity-customization.tsx";
import {AssignmentCopyLinkDialog} from "./assignment-copy-link-modal.tsx";
import AssignmentForm from "./assignment-form.tsx";
import {AIGuideActivityKind} from "./constants.ts";
import NewAssignmentPostButton from "./new-assignment-post-button.tsx";
import QuestionSetRadio from "./question-set-radio.tsx";

import warningIcon from "@phosphor-icons/core/fill/warning-fill.svg";

interface Props {
    /**
     * The assignment data.
     */
    assignment: Assignment;
    /**
     * An array of the qualifiedContentDescriptors to make multiple assignments for each
     * qualifiedContentDescriptors in it.
     */
    qualifiedContentDescriptorsList: Array<string>;
    /**
     * The modal action.
     */
    modalTitle?: NewAssignmentModalTitle;
    /**
     * Refetch the assignment table after update or clear editingAssignment in
     * store.
     */
    onMutationSuccess?: () => void;

    onMutationSubmit?: (params: NewAssignmentSubmitParameters) => unknown;

    /**
     * Called when we updated any of the assignment fields.
     */
    onAssignmentUpdate: (props?: any) => void;
    /**
     * Called when the modal is closed.
     */
    onClose: () => unknown;
    /**
     * Pass the subject slug for creating assignment.
     * From coach dashboard, we should already have the subject slug.
     */
    subjectSlug?: string;
    /**
     * Whether or not the assignment included exercises with grouped assessment
     * items
     */
    hasGroupedExercises: boolean;
    /**
     * Whether ALL, SOME, or NONE of assigner's classrooms are ChildAssignmentsClassrooms
     */
    hasChildAssignmentsClassroom?: UserHasChildAssignmentClassrooms;
}

const strings = {
    /**
     * Primary button label for creating multiple scheduled assignments.
     */
    scheduleMultiple: (itemCount: number) => t`Schedule (${itemCount})`,

    /**
     * Used as the primary button label for the assign modal with multiple
     * contents.
     */
    assignMultiple: (itemCount: number) => t`Assign (${itemCount})`,

    /**
     * Used as the modal title when creating a new assignment.
     */
    assignMultipleTitle: (itemCount: number) =>
        plural(itemCount, {
            one: "Assign # item",
            other: "Assign # items",
        }),
} as const;

const getModalTextMap = () => ({
    COPY: {
        translatedTitle: t`Copy assignment`,
    },
    EDIT: {
        translatedTitle: t`Edit assignment`,
        loadingText: t`Saving changes...`,
    },
    ASSIGN: {
        translatedTitle: t`Assign`,
    },
});

const AssignmentModal = (props: Props): React.ReactNode => {
    const {
        assignment,
        qualifiedContentDescriptorsList,
        modalTitle = "ASSIGN",
        onMutationSuccess,
        onMutationSubmit,
        onAssignmentUpdate,
        onClose,
        subjectSlug,
        hasGroupedExercises,
        hasChildAssignmentsClassroom = "NONE",
    } = props;

    const [
        customActivityAssignButtonEnabled,
        setCustomActivityAssignButtonEnabled,
    ] = useState(true);
    const [modalScreen, setModalScreen] = useState<"ASSIGN" | "SHARE">(
        "ASSIGN",
    );
    const [assignmentDataToShare, setAssignmentDataToShare] = useState<
        Array<Assignment>
    >([]);
    const [
        assignmentDataToShareDoneLoading,
        setAssignmentDataToShareDoneLoading,
    ] = useState(false);

    const featureIsOn = useFeatureIsOn("tx-quick-assign-kad");

    const getButtonText = useCallback((): undefined | string => {
        const {startDate} = assignment;
        const itemCount = qualifiedContentDescriptorsList.length;

        if (modalTitle === "EDIT") {
            return t`Edit (1)`;
        }

        if (isAfterNow(startDate)) {
            if (itemCount) {
                return strings.scheduleMultiple(itemCount);
            }

            return t`Schedule `;
        }

        if (itemCount) {
            return strings.assignMultiple(itemCount);
        }

        return t`Assign`;
    }, [assignment, qualifiedContentDescriptorsList.length, modalTitle]);

    const renderEditWarning = useCallback((): React.ReactNode => {
        return (
            <View
                style={styles.warningBanner}
                testId="assignment-modal-edit-warning"
            >
                <PhosphorIcon
                    icon={warningIcon}
                    color={color.gold}
                    size="medium"
                    style={{marginInlineEnd: spacing.xxSmall_6}}
                />
                <LabelXSmall>
                    <strong>
                        {t`You are editing an existing assignment.`}
                    </strong>{" "}
                    {t`Assignment data will be lost for any students you remove.`}
                </LabelXSmall>
            </View>
        );
    }, []);

    const renderContent = useCallback((): React.ReactNode => {
        const maxDueDate = isWritingCoachAssignment(assignment)
            ? getMaxDueDateWritingCoach().toDate()
            : undefined;

        const itemCount = qualifiedContentDescriptorsList.length;
        const isAIGuideActivity = qualifiedContentDescriptorsList
            .at(-1)
            ?.includes(AIGuideActivityKind);

        const shouldHideQuestionSetRadioForCAC =
            !!assignment.studentList?.isChildAssignmentsClassroom ||
            hasChildAssignmentsClassroom === "ALL";

        // this constant will be true in the following usecases:
        // 1. bulk assigning content to multiple classrooms
        // 2. when no classroom has been selected yet
        // 3. a user has at least 1 child assignments classroom
        const displayStudentAndChildCopy =
            !assignment.studentList && hasChildAssignmentsClassroom !== "NONE";

        return (
            <View style={styles.content}>
                <View style={styles.container}>
                    {!!itemCount && itemCount > 1 && (
                        <Body tag="p" style={styles.subtext}>
                            {t`Settings will apply to all selected assignment items.`}
                        </Body>
                    )}
                    {!assignment.id &&
                        modalTitle === "ASSIGN" &&
                        !shouldHideQuestionSetRadioForCAC && (
                            <>
                                <QuestionSetRadio
                                    assignment={assignment}
                                    onAssignmentUpdate={onAssignmentUpdate}
                                    qualifiedContentDescriptorsList={
                                        qualifiedContentDescriptorsList
                                    }
                                    hasGroupedExercises={!!hasGroupedExercises}
                                    displayStudentAndChildCopy={
                                        displayStudentAndChildCopy
                                    }
                                />
                                {isAIGuideActivity && (
                                    <ActivityCustomization
                                        onAssignmentUpdate={onAssignmentUpdate}
                                        qualifiedContentDescriptorsList={
                                            qualifiedContentDescriptorsList
                                        }
                                        setCustomActivityAssignButtonEnabled={
                                            setCustomActivityAssignButtonEnabled
                                        }
                                    />
                                )}
                            </>
                        )}
                    <AssignmentForm
                        persistSettings={modalTitle === "ASSIGN"}
                        assignment={assignment}
                        qualifiedContentDescriptorsList={
                            qualifiedContentDescriptorsList
                        }
                        maxDueDate={maxDueDate}
                        userHasChildAssignmentsClassrooms={
                            hasChildAssignmentsClassroom
                        }
                    />
                    {modalTitle === "EDIT" && renderEditWarning()}
                </View>
            </View>
        );
    }, [
        assignment,
        qualifiedContentDescriptorsList,
        modalTitle,
        onAssignmentUpdate,
        hasGroupedExercises,
        hasChildAssignmentsClassroom,
        renderEditWarning,
    ]);

    const renderFooter = useCallback((): React.ReactNode => {
        const copy = getModalTextMap()[modalTitle];
        const onMutationSuccessCloseModal = () => {
            if (onMutationSuccess) {
                onMutationSuccess();
            }
            onClose();
        };
        const handleOnMutationSuccess = () => {
            if (onMutationSuccess) {
                onMutationSuccess();
            }
            const quickAssignUrlFlagPass = !!getPass("tx-quick-assign-kad");
            const quickAssignFeatureOn = featureIsOn;
            const showShareModal =
                modalTitle !== "EDIT" &&
                (quickAssignUrlFlagPass || quickAssignFeatureOn);
            if (showShareModal) {
                setModalScreen("SHARE");
            } else {
                onClose();
            }
        };
        const handleOnMutationSubmit = (
            params: NewAssignmentSubmitParameters,
        ) => {
            if (onMutationSubmit) {
                onMutationSubmit(params);
            }
            const {createdAssignments} = params;
            if (createdAssignments) {
                // Only consider successfully created assignments that are assigned
                // to KAD classrooms and are not drafts.
                const assignmentData = (createdAssignments || []).filter(
                    (createdAssignment) =>
                        !createdAssignment.isDraft &&
                        !!createdAssignment.studentList?.isK4dClassroom,
                );
                setAssignmentDataToShare(assignmentData);
                setAssignmentDataToShareDoneLoading(true);
                // If we are done loading assignment data
                // and there is no assignment data to share, close the modal.
                if (assignmentData.length === 0) {
                    onClose();
                }
            }
        };

        return (
            <View style={styles.row}>
                {modalTitle === "EDIT" && (
                    <Button kind="tertiary" onClick={onClose}>
                        {t`Cancel`}
                    </Button>
                )}
                {modalTitle !== "EDIT" && (
                    <NewAssignmentPostButton
                        onMutationSubmit={onMutationSubmit}
                        onMutationSuccess={onMutationSuccessCloseModal}
                        isDraft={true}
                        disabled={!assignment.isDraft}
                        subjectSlug={subjectSlug}
                    />
                )}
                <Strut size={spacing.medium_16} />
                <NewAssignmentPostButton
                    onMutationSubmit={handleOnMutationSubmit}
                    onMutationSuccess={handleOnMutationSuccess}
                    disabled={!customActivityAssignButtonEnabled}
                    isDraft={false}
                    buttonText={getButtonText()}
                    loadingText={
                        "loadingText" in copy ? copy.loadingText : undefined
                    }
                    subjectSlug={subjectSlug}
                />
            </View>
        );
    }, [
        modalTitle,
        onMutationSuccess,
        onClose,
        featureIsOn,
        onMutationSubmit,
        assignment.isDraft,
        subjectSlug,
        customActivityAssignButtonEnabled,
        getButtonText,
    ]);

    const renderAssignModal = useCallback((): React.ReactNode => {
        const itemCount = qualifiedContentDescriptorsList.length;
        const title =
            modalTitle === "ASSIGN" && itemCount
                ? strings.assignMultipleTitle(itemCount)
                : getModalTextMap()[modalTitle].translatedTitle;

        return (
            <OnePaneDialog
                testId="assignment-modal"
                title={title}
                content={renderContent()}
                footer={renderFooter()}
                style={styles.dialog}
            />
        );
    }, [
        modalTitle,
        qualifiedContentDescriptorsList.length,
        renderContent,
        renderFooter,
    ]);

    const quickAssignUrlFlagPass = !!getPass("tx-quick-assign-kad");
    const quickAssignFeatureOn = featureIsOn;
    const showShareModal =
        modalTitle !== "EDIT" &&
        (quickAssignUrlFlagPass || quickAssignFeatureOn);
    // Share Modal is visible if:
    // * the feature is enabled AND
    // * we are in the SHARE state AND
    // * either we are not done loading assignment data
    //   or we are done loading AND have more than one assignment.
    return (
        <>
            {modalScreen === "ASSIGN" && renderAssignModal()}
            {modalScreen === "SHARE" &&
                showShareModal &&
                (!assignmentDataToShareDoneLoading ||
                    (assignmentDataToShareDoneLoading &&
                        assignmentDataToShare.length > 0)) && (
                    <AssignmentCopyLinkDialog
                        assignments={assignmentDataToShare}
                        cedarActionType={modalTitle}
                    />
                )}
        </>
    );
};

const styles = StyleSheet.create({
    dialog: {
        maxHeight: 680,
        maxWidth: 750,
        [mediaQueries.smOrSmaller]: {
            maxHeight: "100%",
        },
    },
    content: {
        flex: 1,
        justifyContent: "space-between",
    },
    container: {
        maxWidth: "100%",
        [mediaQueries.smOrSmaller]: {
            height: "100%",
        },
        zIndex: 1,
    },
    subtext: {
        marginBlockStart: 0,
    },
    row: {
        flexDirection: "row",
        justifyContent: "flex-end",
        width: "100%",
    },
    warningBanner: {
        background: color.offWhite,
        border: `1px solid ${color.offBlack16}`,
        borderRadius: spacing.xxxSmall_4,
        flexDirection: "row",
        padding: spacing.large_24,
    },
});

export default AssignmentModal;
